import * as React from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireRole } from "@/components/auth/RequireRole";
import { RequireMultitenant } from "@/components/auth/RequireMultitenant";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { RequireModule } from "@/components/layout/RequireModule";
import { MODULE_GROUP_DEFENDER, MODULE_SECURITY_SCAN } from "@/lib/api/modules";
import { PostureIndexRedirect, PostureShell } from "@/features/posture/PostureShell";
import { TenantProvider } from "@/lib/tenant";
import {
  DefaultTenantRedirect,
  LegacyRedirect,
} from "@/components/layout/TenantRedirects";
import { LegacySettingsRedirect } from "@/features/admin/AdminTenantPage";
import { LEGACY_SETTINGS_PAGES } from "@/features/admin/tenantConnection";

/**
 * The route table.
 *
 * Built at MODULE SCOPE with createBrowserRouter, so the router is constructed
 * once for the life of the tab and a re-render of <App> cannot reset the
 * history stack.
 *
 * Every page is code-split. `withSuspense` gives each one a skeleton while its
 * chunk loads and an error boundary if the chunk fails - a failed dynamic
 * import is a real thing (a stale index.html after a deploy) and it must show
 * an error, not a blank page.
 *
 * Every page module below must have a DEFAULT EXPORT.
 */

function withSuspense(
  Component: React.LazyExoticComponent<React.ComponentType>,
  label: string,
): React.ReactElement {
  return (
    <ErrorBoundary label={label}>
      <React.Suspense fallback={<LoadingSkeleton variant="page" label={`Loading ${label}`} />}>
        <Component />
      </React.Suspense>
    </ErrorBoundary>
  );
}

const Login = React.lazy(() => import("@/pages/Login"));
const Overview = React.lazy(() => import("@/pages/Overview"));

const InventoryIndex = React.lazy(() => import("@/pages/inventory/InventoryIndex"));
const InventoryList = React.lazy(() => import("@/pages/inventory/InventoryList"));
const InventoryDetail = React.lazy(() => import("@/pages/inventory/InventoryDetail"));

const PostureFindings = React.lazy(() => import("@/pages/posture/PostureFindings"));
const PostureCriticalHigh = React.lazy(() => import("@/pages/posture/PostureCriticalHigh"));
const PostureMediumLow = React.lazy(() => import("@/pages/posture/PostureMediumLow"));
const PostureBaseline = React.lazy(() => import("@/pages/posture/PostureBaseline"));
const PostureSecureScore = React.lazy(() => import("@/pages/posture/PostureSecureScore"));
const PostureAiBp = React.lazy(() => import("@/pages/posture/PostureAiBp"));
const PostureManual = React.lazy(() => import("@/pages/posture/PostureManual"));
const PostureExceptions = React.lazy(() => import("@/pages/posture/PostureExceptions"));
const CheckDetail = React.lazy(() => import("@/pages/posture/CheckDetail"));

const DefenderPage = React.lazy(() => import("@/pages/DefenderPage"));
const EmergencyAccess = React.lazy(() => import("@/pages/admin/settings/emergency-access"));

const Changes = React.lazy(() => import("@/pages/Changes"));
const HistoryPage = React.lazy(() => import("@/pages/HistoryPage"));
const Scans = React.lazy(() => import("@/pages/scans/Scans"));
const ScanDetail = React.lazy(() => import("@/pages/scans/ScanDetail"));
const Reports = React.lazy(() => import("@/pages/Reports"));

const AdminTenant = React.lazy(() => import("@/pages/admin/Tenant"));
// One settings page per subject, grouped default / extended. The old single
// scrolling screen put retention next to scoring next to the tenant credential
// and made every setting look like the same kind of decision.
// Tenant connection merged five pages (Entra application, Tenant init,
// Capabilities, Permissions, Admin consent) into four expandable blocks.
const SettingsTenantConnection = React.lazy(
  () => import("@/pages/admin/settings/tenant-connection"),
);
const SettingsScanning = React.lazy(() => import("@/pages/admin/settings/scanning"));
const SettingsScoring = React.lazy(() => import("@/pages/admin/settings/scoring"));
const SettingsThresholds = React.lazy(() => import("@/pages/admin/settings/thresholds"));
const SettingsRetention = React.lazy(() => import("@/pages/admin/settings/retention"));
const SettingsReporting = React.lazy(() => import("@/pages/admin/settings/reporting"));
const SettingsPreview = React.lazy(() => import("@/pages/admin/settings/preview"));
const SettingsModules = React.lazy(() => import("@/pages/admin/settings/modules"));
const AdminUsers = React.lazy(() => import("@/pages/admin/Users"));
const AdminAudit = React.lazy(() => import("@/pages/admin/Audit"));
const PlatformSettings = React.lazy(() => import("@/pages/admin/Platform"));

// The two cross-tenant screens. They belong to the deployment, so they hang
// off the platform shell and never carry a /t/{slug} prefix.
const TenantsOverview = React.lazy(() => import("@/pages/tenants/TenantsOverview"));
const TenantsManage = React.lazy(() => import("@/pages/tenants/TenantsManage"));

const Forbidden = React.lazy(() => import("@/pages/Forbidden"));
const NotFound = React.lazy(() => import("@/pages/NotFound"));

export const routes: RouteObject[] = [
  // Public. /login must NOT sit inside RequireAuth, or a signed-out operator
  // is redirected to the page that redirects them.
  { path: "/login", element: withSuspense(Login, "the sign-in page") },
  { path: "/403", element: withSuspense(Forbidden, "the access-denied page") },
  { path: "/404", element: withSuspense(NotFound, "the not-found page") },

  /*
    The PLATFORM shell: the pages that are about this deployment rather than
    about one tenant — the tenant list, the operators, the audit trail and the
    deployment-wide settings. It has no tenant, so it must not render a tenant
    name or a last-scan indicator, and its links carry no /t/{slug} prefix.
  */
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppShell variant="platform" />
      </RequireAuth>
    ),
    children: [
      // Where "/" goes depends on the multitenant switch, which is a question
      // for the server. <DefaultTenantRedirect> asks it rather than guessing.
      { index: true, element: <DefaultTenantRedirect /> },
      { path: "tenants", element: withSuspense(TenantsOverview, "the tenants overview") },
      {
        path: "tenants/manage",
        element: <RequireRole min="admin" />,
        children: [
          {
            element: <RequireMultitenant />,
            children: [{ index: true, element: withSuspense(TenantsManage, "the tenant list") }],
          },
        ],
      },
      {
        path: "admin",
        element: <RequireRole min="admin" />,
        children: [
          { path: "users", element: withSuspense(AdminUsers, "the operators") },
          { path: "audit", element: withSuspense(AdminAudit, "the audit log") },
          { path: "platform", element: withSuspense(PlatformSettings, "the platform settings") },
        ],
      },
    ],
  },

  /*
    The TENANT shell. Everything under it is one tenant's console, and the
    tenant is in the URL and nowhere else.

    <TenantProvider> resolves the slug BEFORE any child renders: a slug this
    deployment does not hold is a 404, not a silent fall back to the default
    tenant. The children below are the route table this console has always
    had, moved wholesale under the prefix.
  */
  {
    path: "/t/:tenant",
    element: (
      <RequireAuth>
        <TenantProvider>
          <AppShell />
        </TenantProvider>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="overview" replace /> },

      { path: "overview", element: withSuspense(Overview, "the overview") },

      { path: "inventory", element: withSuspense(InventoryIndex, "the inventory") },
      { path: "inventory/:kind", element: withSuspense(InventoryList, "the inventory list") },
      {
        path: "inventory/:kind/:id",
        element: withSuspense(InventoryDetail, "the inventory object"),
      },

      // The posture surface belongs to the security scan module. Navigation to
      // it is already omitted when the module is off; this catches the URL,
      // which navigation cannot — a bookmark or a link in a ticket still
      // arrives here.
      {
        element: <RequireModule group={MODULE_GROUP_DEFENDER} />,
        children: [{ path: "defender", element: withSuspense(DefenderPage, "the Defender modules") }],
      },
      {
        element: <RequireModule module={MODULE_SECURITY_SCAN} />,
        children: [
          // /posture is not a tab (D27): it redirects to the tab a legacy
          // ?tab= names, to All findings when it carries a findings filter,
          // and otherwise to the default tab, Critical & high.
          { path: "posture", element: <PostureIndexRedirect /> },
          // The tabs share PostureShell as a LAYOUT route: it owns the
          // heading, the tab bar and the help button, and renders each tab
          // through its <Outlet/>. Registering the tabs flat drops all three -
          // the shell is not something a tab body renders for itself.
          {
            element: <PostureShell />,
            children: [
              {
                path: "posture/critical-high",
                element: withSuspense(PostureCriticalHigh, "the Critical & high findings"),
              },
              {
                path: "posture/medium-low",
                element: withSuspense(PostureMediumLow, "the Medium & low findings"),
              },
              { path: "posture/all", element: withSuspense(PostureFindings, "the posture findings") },
              {
                path: "posture/baseline",
                element: withSuspense(PostureBaseline, "the Microsoft baseline posture"),
              },
              {
                path: "posture/secure-score",
                element: withSuspense(PostureSecureScore, "the Microsoft Secure Score"),
              },
              // AI best practice belongs to its own module as well as to the
              // security scan. With the module off the tab stays and its view
              // says exactly "Switched off" and where to switch it on (D25),
              // without requesting the assessment the server would refuse.
              {
                path: "posture/ai-bp",
                element: withSuspense(PostureAiBp, "the AI best practice posture"),
              },
              { path: "posture/manual", element: withSuspense(PostureManual, "the manual lane") },
              {
                path: "posture/exceptions",
                element: withSuspense(PostureExceptions, "the accepted risks"),
              },
            ],
          },
          // The Foundations benchmark (CIS references) tab is gone (D22,
          // 2026-10-05): the framework was folded into the sp-ms365 rule set.
          // A bookmarked /posture/cis lands on All findings instead of a 404.
          // Keep this redirect for ONE release, then delete it.
          { path: "posture/cis", element: <Navigate to="../all" relative="path" replace /> },
          // `check` is a fixed segment so a check id can never be mistaken for
          // one of the tabs above - the same ordering discipline the Go route
          // table states for /posture/baseline and friends.
          { path: "posture/check/:checkId", element: withSuspense(CheckDetail, "the check") },
        ],
      },

      { path: "changes", element: withSuspense(Changes, "the change feed") },
      { path: "history", element: withSuspense(HistoryPage, "the history") },
      { path: "scans", element: withSuspense(Scans, "the scan runs") },
      { path: "scans/:runId", element: withSuspense(ScanDetail, "the scan run") },
      { path: "reports", element: withSuspense(Reports, "the reports") },

      // Admin. Guarded here AND server-side by auth.RouteGuards at TierAdmin;
      // the navigation to these routes is not even rendered for non-admins.
      {
        path: "admin",
        element: <RequireRole min="admin" />,
        children: [
          { index: true, element: <Navigate to="settings" replace /> },
          // Kept as redirects into the Tenant connection page; see AdminTenantPage.
          { path: "tenant", element: withSuspense(AdminTenant, "tenant and access") },
          { path: "tenant/:tab", element: withSuspense(AdminTenant, "tenant and access") },
          // The bare /admin/settings keeps working and lands on the first page,
          // so every existing deep link and bookmark still resolves.
          {
            path: "settings",
            element: <Navigate to="tenant-connection" replace />,
          },
          {
            path: "settings/tenant-connection",
            element: withSuspense(SettingsTenantConnection, "the tenant connection settings"),
          },
          // The five pages Tenant connection replaced. Each lands on its block,
          // query string kept: the server's admin-consent return still arrives
          // at settings/consent?admin_consent=… (auth/handlers.go).
          ...Object.entries(LEGACY_SETTINGS_PAGES).map(([page, anchor]) => ({
            path: `settings/${page}`,
            element: <LegacySettingsRedirect anchor={anchor} />,
          })),
          {
            path: "settings/scanning",
            element: withSuspense(SettingsScanning, "the scanning settings"),
          },
          {
            path: "settings/scoring",
            element: withSuspense(SettingsScoring, "the scoring settings"),
          },
          {
            path: "settings/thresholds",
            element: withSuspense(SettingsThresholds, "the threshold settings"),
          },
          {
            path: "settings/retention",
            element: withSuspense(SettingsRetention, "the retention settings"),
          },
          {
            path: "settings/reporting",
            element: withSuspense(SettingsReporting, "the reporting settings"),
          },
          {
            path: "settings/preview",
            element: withSuspense(SettingsPreview, "the preview API settings"),
          },
          {
            path: "settings/modules",
            element: withSuspense(SettingsModules, "the module settings"),
          },
          {
            path: "settings/emergency-access",
            element: withSuspense(EmergencyAccess, "the emergency-access accounts"),
          },
        ],
      },

      { path: "*", element: withSuspense(NotFound, "the not-found page") },
    ],
  },

  /*
    Every address from before the tenant prefix existed.

    A legacy path is REDIRECTED to the default tenant, search string and all,
    rather than served: the API's unprefixed alias still answers, but leaving
    the browser on /overview would mean a bookmark that silently means "the
    default tenant" forever, and a link that means a different tenant to the
    person who pasted it. Anything that is not a known legacy path is a 404.
  */
  { path: "*", element: <LegacyRedirect /> },
];

export const router = createBrowserRouter(routes);
